import { OXYGEN_GENERATOR_MAX_LEVEL, OXYGEN_GENERATOR_MIN_LEVEL, OXYGEN_CREW_USE_PER_MINUTE, OXYGEN_ENGINE_USE_FACTOR, OXYGEN_GENERATION_PER_LEVEL_PER_MINUTE, OXYGEN_PROCESSING_USE_PER_MINUTE, LIQUID_OXYGEN_EXCESS_THRESHOLD, VENT_PROXIMITY_TEMPERATURE_THRESHOLD} from '../../shared/constants.js';
import type { ControlInput, GameSnapshot } from '../../shared/types.js';
import { state as controllerState } from '../controllerState.js';
import { HEATER_LEVELS, HEATER_POWER_MW, NOISE_AUDIBLE_THRESHOLD, NOISE_BAND_COUNT, NOISE_MONITOR_UPGRADE_ID } from '../../shared/constants.js';
import { clamp, createCard, createPlusMinusControl, getConsoleLineWidth, padDots } from '../controllerUiHelpers.js';

type CreateLifeSupportRoomControllerOptions = {
  controls: HTMLDivElement | null;
  ctrlZoneLeftBottom: HTMLDivElement | null;
  ctrlZoneRightAux: HTMLDivElement | null;
  emitControl: (input: ControlInput) => void;
  formatConsoleOutput: (snapshot: GameSnapshot) => string;
};

type LifeSupportTabId = 'oxygen' | 'temp' | 'noise';

const LIFE_SUPPORT_TABS: Array<{ id: LifeSupportTabId; label: string }> = [
  { id: 'oxygen', label: 'Oxygen' },
  { id: 'temp', label: 'Heater' },
  { id: 'noise', label: 'Noise' }
];

export const createLifeSupportRoomController = ({
  controls,
  ctrlZoneLeftBottom,
  ctrlZoneRightAux,
  emitControl,
  formatConsoleOutput
}: CreateLifeSupportRoomControllerOptions) => {
  let oxygenGeneratorLevelValue: HTMLElement | null = null;
  let oxygenGeneratorDecreaseButton: HTMLButtonElement | null = null;
  let oxygenGeneratorIncreaseButton: HTMLButtonElement | null = null;
  let oxygenConsoleOutput: HTMLElement | null = null;
  let insideThermFill: HTMLElement | null = null;
  let insideThermLabel: HTMLElement | null = null;
  let outsideThermFill: HTMLElement | null = null;
  let outsideThermLabel: HTMLElement | null = null;
  let insideThermTarget: HTMLElement | null = null;
  let heaterPowerValue: HTMLElement | null = null;
  /** Heater controls (null until installed): level +/- buttons, 5 level segments and a status line. */
  let heaterUi: {
    decrease: HTMLButtonElement;
    increase: HTMLButtonElement;
    segments: HTMLElement[];
    status: HTMLElement;
  } | null = null;
  let activeTab: LifeSupportTabId = 'oxygen';
  const tabButtons = new Map<LifeSupportTabId, HTMLButtonElement>();
  const tabPanels = new Map<LifeSupportTabId, HTMLElement>();

  const setActiveTab = (tab: LifeSupportTabId): void => {
    activeTab = tab;
    for (const [id, button] of tabButtons) {
      button.classList.toggle('on', id === tab);
    }
    for (const [id, panel] of tabPanels) {
      panel.classList.toggle('active', id === tab);
    }
    startNoiseLoop();
  };

  const addScrewCorners = (el: HTMLElement): void => {
    for (let i = 0; i < 4; i++) {
      const screw = document.createElement('span');
      screw.className = 'panel-screw';
      el.append(screw);
    }
  };

  // ---------------------------------------------------------------------------
  // Noise tab: a frequency-analyser view of everything the hull is broadcasting.
  // Server-side model: src/shared/signature.ts (bands low -> high: engine/thrust, pumps/ballast, lamps/pings).
  // ---------------------------------------------------------------------------
  const NOISE_SOURCE_LABELS: Array<{ key: 'engine' | 'thrust' | 'pumps' | 'lights' | 'ping' | 'heat'; label: string }> = [
    { key: 'engine', label: 'ENGINE' },
    { key: 'thrust', label: 'THRUST' },
    { key: 'pumps', label: 'PUMPS / BALLAST' },
    { key: 'lights', label: 'LAMPS' },
    { key: 'ping', label: 'SONAR PING' },
    { key: 'heat', label: 'HEAT (SILENT)' }
  ];
  const NOISE_SMOOTHING = 0.22;
  const NOISE_PEAK_FALL_PER_FRAME = 0.6;
  const NOISE_CANVAS_HEIGHT_PX = 240;

  let noiseUi: {
    locked: HTMLElement;
    live: HTMLElement;
    canvas: HTMLCanvasElement;
    signature: HTMLElement;
    verdict: HTMLElement;
    attachments: HTMLElement;
    sourceRows: Map<string, { row: HTMLElement; value: HTMLElement; fill: HTMLElement }>;
  } | null = null;
  let noiseTarget: number[] = new Array<number>(NOISE_BAND_COUNT).fill(0);
  const noiseDisplay: number[] = new Array<number>(NOISE_BAND_COUNT).fill(0);
  const noisePeaks: number[] = new Array<number>(NOISE_BAND_COUNT).fill(0);
  let noiseFrame = 0;

  const noiseInstalled = (): boolean =>
    Boolean(controllerState.lastRoomState?.ownedUpgrades.includes(NOISE_MONITOR_UPGRADE_ID));

  const drawNoise = (timeMs: number): void => {
    const ui = noiseUi;
    if (!ui || !ui.canvas.isConnected || activeTab !== 'noise' || !noiseInstalled()) {
      noiseFrame = 0;
      return;
    }
    noiseFrame = requestAnimationFrame(drawNoise);

    const canvas = ui.canvas;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const cssWidth = Math.max(160, canvas.clientWidth);
    const cssHeight = NOISE_CANVAS_HEIGHT_PX;
    if (canvas.width !== Math.round(cssWidth * dpr) || canvas.height !== Math.round(cssHeight * dpr)) {
      canvas.width = Math.round(cssWidth * dpr);
      canvas.height = Math.round(cssHeight * dpr);
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssWidth, cssHeight);

    const padL = 30, padR = 8, padT = 10, padB = 22;
    const plotW = cssWidth - padL - padR;
    const plotH = cssHeight - padT - padB;
    const yFor = (level: number): number => padT + plotH * (1 - clamp(level, 0, 100) / 100);

    // Grid and axis.
    ctx.font = '10px monospace';
    ctx.textBaseline = 'middle';
    ctx.lineWidth = 1;
    for (const mark of [0, 25, 50, 75, 100]) {
      const y = Math.round(yFor(mark)) + 0.5;
      ctx.strokeStyle = 'rgba(82,188,214,0.14)';
      ctx.beginPath(); ctx.moveTo(padL, y); ctx.lineTo(padL + plotW, y); ctx.stroke();
      ctx.fillStyle = 'rgba(173,224,232,0.55)';
      ctx.textAlign = 'right';
      ctx.fillText(String(mark), padL - 5, y);
    }
    ctx.textAlign = 'center';
    ctx.fillStyle = 'rgba(173,224,232,0.55)';
    ctx.fillText('LOW', padL + plotW * 0.1, cssHeight - 8);
    ctx.fillText('MID', padL + plotW * 0.5, cssHeight - 8);
    ctx.fillText('HIGH', padL + plotW * 0.9, cssHeight - 8);

    const pulse = 0.5 + 0.5 * Math.sin(timeMs / 140);
    const slot = plotW / NOISE_BAND_COUNT;
    const barW = Math.max(3, slot * 0.7);
    for (let i = 0; i < NOISE_BAND_COUNT; i += 1) {
      noiseDisplay[i] += (noiseTarget[i] - noiseDisplay[i]) * NOISE_SMOOTHING;
      noisePeaks[i] = Math.max(noiseDisplay[i], noisePeaks[i] - NOISE_PEAK_FALL_PER_FRAME);
      const level = noiseDisplay[i];
      const x = padL + slot * i + (slot - barW) / 2;
      const top = yFor(level);
      const loud = level >= NOISE_AUDIBLE_THRESHOLD;
      const gradient = ctx.createLinearGradient(0, padT + plotH, 0, top);
      if (loud) {
        const glow = 0.65 + 0.35 * pulse;
        gradient.addColorStop(0, `rgba(255,154,60,${0.55 * glow})`);
        gradient.addColorStop(1, `rgba(255,${Math.round(90 + 40 * pulse)},70,${glow})`);
        ctx.shadowColor = 'rgba(255,100,70,0.75)';
        ctx.shadowBlur = 6 + 6 * pulse;
      } else {
        gradient.addColorStop(0, 'rgba(36,120,150,0.55)');
        gradient.addColorStop(1, 'rgba(110,226,242,0.95)');
        ctx.shadowBlur = 0;
      }
      ctx.fillStyle = gradient;
      ctx.fillRect(x, top, barW, padT + plotH - top);
      ctx.shadowBlur = 0;
      // Peak-hold cap.
      ctx.fillStyle = noisePeaks[i] >= NOISE_AUDIBLE_THRESHOLD ? '#ffb39a' : '#bde4f0';
      ctx.fillRect(x, yFor(noisePeaks[i]) - 2, barW, 2);
    }

    // The audible threshold.
    const thresholdY = Math.round(yFor(NOISE_AUDIBLE_THRESHOLD)) + 0.5;
    ctx.strokeStyle = '#ff7a5c';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([6, 4]);
    ctx.beginPath(); ctx.moveTo(padL, thresholdY); ctx.lineTo(padL + plotW, thresholdY); ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = '#ff9a80';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'bottom';
    ctx.fillText('CREATURES CAN HEAR YOU', padL + plotW, thresholdY - 3);
  };

  const startNoiseLoop = (): void => {
    if (noiseFrame === 0 && noiseUi && activeTab === 'noise' && noiseInstalled()) {
      noiseFrame = requestAnimationFrame(drawNoise);
    }
  };

  const updateNoiseUi = (snapshot: GameSnapshot): void => {
    if (!noiseUi) return;
    const installed = noiseInstalled();
    noiseUi.locked.style.display = installed ? 'none' : 'block';
    noiseUi.live.style.display = installed ? 'flex' : 'none';
    if (!installed) return;
    const submarine = snapshot.submarine;
    noiseTarget = submarine.noiseBands && submarine.noiseBands.length === NOISE_BAND_COUNT
      ? submarine.noiseBands
      : new Array<number>(NOISE_BAND_COUNT).fill(0);
    const signature = Math.round(submarine.signature ?? 0);
    const audible = signature >= NOISE_AUDIBLE_THRESHOLD;
    noiseUi.signature.textContent = `${signature}`;
    noiseUi.signature.classList.toggle('noise-hot', audible);
    noiseUi.verdict.textContent = submarine.docked
      ? 'DOCKED · SHIELDED'
      : audible ? 'AUDIBLE · CREATURES CAN HEAR YOU' : signature > 0 ? 'QUIET · BELOW HEARING' : 'SILENT RUNNING';
    noiseUi.verdict.classList.toggle('noise-hot', audible);
    const attached = submarine.hullAttachments ?? 0;
    noiseUi.attachments.textContent = attached > 0 ? `${attached} ON HULL` : 'HULL CLEAR';
    noiseUi.attachments.classList.toggle('noise-hot', attached > 0);
    const sources = submarine.noiseSources;
    for (const { key } of NOISE_SOURCE_LABELS) {
      const entry = noiseUi.sourceRows.get(key);
      if (!entry) continue;
      const value = sources ? sources[key] : 0;
      entry.value.textContent = `${value}`;
      entry.fill.style.width = `${clamp(value, 0, 100)}%`;
      entry.row.classList.toggle('active', value > 0);
      entry.fill.classList.toggle('noise-hot', key !== 'heat' && value >= NOISE_AUDIBLE_THRESHOLD);
    }
    startNoiseLoop();
  };

  const buildNoisePanel = (): HTMLElement => {
    const card = createCard('Acoustic noise', 'Live spectrum of what the hull is broadcasting. Bars above the dashed line carry far enough for creatures to hear.');
    card.classList.add('ctrl-room-main-card', 'life-support-noise-card');
    addScrewCorners(card);

    const locked = document.createElement('div');
    locked.className = 'noise-locked';
    const lockedTitle = document.createElement('div');
    lockedTitle.className = 'noise-locked-title';
    lockedTitle.textContent = 'NOISE MONITOR OFFLINE';
    const lockedText = document.createElement('div');
    lockedText.className = 'noise-locked-text';
    lockedText.textContent = 'Requires upgrade: Acoustic Noise Monitor. Build it in the storage room, or buy it at a base.';
    locked.append(lockedTitle, lockedText);

    const live = document.createElement('div');
    live.className = 'noise-live';
    const canvas = document.createElement('canvas');
    canvas.className = 'noise-canvas';
    canvas.style.cssText = `width:100%;height:${NOISE_CANVAS_HEIGHT_PX}px;display:block;`;

    const header = document.createElement('div');
    header.className = 'noise-header';
    const signatureBlock = document.createElement('div');
    signatureBlock.className = 'noise-signature-block';
    const signatureLabel = document.createElement('span');
    signatureLabel.className = 'noise-signature-label';
    signatureLabel.textContent = 'SIGNATURE';
    const signature = document.createElement('span');
    signature.className = 'noise-signature';
    signature.textContent = '0';
    signatureBlock.append(signatureLabel, signature);
    const verdict = document.createElement('div');
    verdict.className = 'noise-verdict';
    verdict.textContent = 'SILENT RUNNING';
    const attachments = document.createElement('div');
    attachments.className = 'noise-attachments';
    attachments.textContent = 'HULL CLEAR';
    header.append(signatureBlock, verdict, attachments);

    const sourceList = document.createElement('div');
    sourceList.className = 'noise-sources';
    const sourceRows = new Map<string, { row: HTMLElement; value: HTMLElement; fill: HTMLElement }>();
    for (const { key, label } of NOISE_SOURCE_LABELS) {
      const row = document.createElement('div');
      row.className = 'noise-source-row';
      const name = document.createElement('span');
      name.className = 'noise-source-name';
      name.textContent = label;
      const meter = document.createElement('span');
      meter.className = 'noise-source-meter';
      const fill = document.createElement('span');
      fill.className = 'noise-source-fill';
      meter.append(fill);
      const value = document.createElement('span');
      value.className = 'noise-source-value';
      value.textContent = '0';
      row.append(name, meter, value);
      sourceList.append(row);
      sourceRows.set(key, { row, value, fill });
    }

    live.append(header, canvas, sourceList);
    card.append(locked, live);
    noiseUi = { locked, live, canvas, signature, verdict, attachments, sourceRows };
    return card;
  };

  const THERM_MIN = -100;
  const THERM_MAX = 100;
  const tempToPercent = (t: number): number =>
    Math.min(100, Math.max(0, ((t - THERM_MIN) / (THERM_MAX - THERM_MIN)) * 100));

  const updateThermometer = (fill: HTMLElement | null, label: HTMLElement | null, temp: number, target?: { marker: HTMLElement | null; value: number | undefined }): void => {
    if (target?.marker) {
      target.marker.style.display = target.value === undefined ? 'none' : 'block';
      if (target.value !== undefined) {
        target.marker.style.bottom = `${tempToPercent(target.value)}%`;
        target.marker.title = `Heading toward ${target.value.toFixed(0)}°C`;
      }
    }
    if (fill) {
      fill.style.height = `${tempToPercent(temp)}%`;
      const cold = temp < 0;
      const hot = temp > 40;
      fill.classList.toggle('therm-fill-cold', cold);
      fill.classList.toggle('therm-fill-hot', hot);
    }
    if (label) {
      label.textContent = target?.value !== undefined
        ? `${temp.toFixed(0)}°C → ${target.value.toFixed(0)}°C`
        : `${temp.toFixed(0)}°C`;
    }
  };

  const updateUi = (snapshot: GameSnapshot | null): void => {
    if (!snapshot) {
      return;
    }

    if (oxygenGeneratorLevelValue) {
      oxygenGeneratorLevelValue.textContent = `${Math.round(snapshot.submarine.oxygenGeneratorLevel)}%`;
    }
    if (oxygenGeneratorDecreaseButton || oxygenGeneratorIncreaseButton) {
      const roundedLevel = Math.round(snapshot.submarine.oxygenGeneratorLevel);
      if (oxygenGeneratorDecreaseButton) {
        oxygenGeneratorDecreaseButton.disabled = !controllerState.lastRoomState?.ownedUpgrades.includes('unlock-oxygen') || roundedLevel <= OXYGEN_GENERATOR_MIN_LEVEL;
      }
      if (oxygenGeneratorIncreaseButton) {
        oxygenGeneratorIncreaseButton.disabled = !controllerState.lastRoomState?.ownedUpgrades.includes('unlock-oxygen') || roundedLevel >= OXYGEN_GENERATOR_MAX_LEVEL;
      }
    }
    if (oxygenConsoleOutput) {
      oxygenConsoleOutput.textContent = formatConsoleOutput(snapshot);
    }
    updateNoiseUi(snapshot);
    updateThermometer(insideThermFill, insideThermLabel, snapshot.submarine.insideTemperature, { marker: insideThermTarget, value: snapshot.submarine.insideTemperatureTarget });
    updateThermometer(outsideThermFill, outsideThermLabel, snapshot.submarine.outsideTemperature);
    if (heaterUi) {
      const { heaterActive } = snapshot.submarine;
      const level = snapshot.submarine.heaterLevel ?? (snapshot.submarine.heaterOn ? 3 : 0);
      heaterUi.segments.forEach((segment, index) => {
        const lit = index < level;
        segment.style.background = lit ? (heaterActive ? '#ff9a3c' : '#c27a2e') : 'rgba(255,255,255,0.08)';
        segment.style.boxShadow = lit && heaterActive ? '0 0 8px rgba(255,154,60,0.7)' : 'none';
      });
      if (heaterPowerValue) {
        const mw = heaterActive ? HEATER_POWER_MW * (level / HEATER_LEVELS) : 0;
        heaterPowerValue.textContent = `${mw.toFixed(1)} MW`;
      }
      heaterUi.decrease.disabled = level <= 0;
      heaterUi.increase.disabled = level >= HEATER_LEVELS;
      heaterUi.status.textContent = level === 0 ? 'OFF' : `LEVEL ${level} / ${HEATER_LEVELS} · ${heaterActive ? 'HEATING' : 'HOLDING'}`;
    }
  };

  const render = (): void => {
    if (!controls) {
      return;
    }

    const snapshot = controllerState.lastSnapshot;
    const submarine = snapshot?.submarine ?? { oxygen: 100, oxygenGeneratorLevel: 100, engineOutput: 100, docked: false, engineOverloaded: false, insideTemperature: 0, outsideTemperature: 0, heaterLevel: 0, heaterOn: false, heaterActive: false };

    const initialReadout = snapshot ? formatConsoleOutput(snapshot) : [
      '================================================',
      'Current Tank Level:.......................100%',
      'O2 Generation per minute:.................50.0/min',
      'O2 User per minute:.......................0.0/min',
      'Time until empty:.........................N/A',
      '',
      '================================================',
      'O2 use by subsystem:',
      '  - crew .............................................0.0/min',
      '  - engine............................................0.0/min',
      '  - material processor:...............................0.0/min'
    ].join('\n');

    const card = createCard('Life Support', 'CRT console display for tank and subsystem oxygen diagnostics.');
    card.classList.add('ctrl-room-main-card', 'room-console-card');

    const output = document.createElement('pre');
    output.className = 'room-console-output';
    output.textContent = initialReadout;
    oxygenConsoleOutput = output;

    const cursor = document.createElement('span');
    cursor.className = 'room-console-cursor';
    cursor.textContent = '_';

    card.append(output, cursor);

    const temperatureCard = createCard('Thermal status', 'Monitor internal cabin temperature and outside water temperature.');
    temperatureCard.classList.add('ctrl-room-aux-card', 'life-support-thermal-card');
    addScrewCorners(temperatureCard);

    const createThermometer = (label: string): { wrap: HTMLElement; fill: HTMLElement; tempLabel: HTMLElement; targetMarker: HTMLElement } => {
      const wrap = document.createElement('div');
      wrap.className = 'therm-wrap';

      const title = document.createElement('div');
      title.className = 'therm-title';
      title.textContent = label;

      const tube = document.createElement('div');
      tube.className = 'therm-tube';

      const scaleEl = document.createElement('div');
      scaleEl.className = 'therm-scale';
      for (const tick of [100, 50, 0, -50, -100]) {
        const tickEl = document.createElement('div');
        tickEl.className = 'therm-tick';
        const pct = ((tick - THERM_MIN) / (THERM_MAX - THERM_MIN)) * 100;
        tickEl.style.bottom = `${pct}%`;
        const tickLabel = document.createElement('span');
        tickLabel.textContent = `${tick}`;
        tickEl.append(tickLabel);
        scaleEl.append(tickEl);
      }

      const track = document.createElement('div');
      track.className = 'therm-track';

      const fill = document.createElement('div');
      fill.className = 'therm-fill';
      fill.style.height = '50%';

      const bulb = document.createElement('div');
      bulb.className = 'therm-bulb';

      const targetMarker = document.createElement('div');
      targetMarker.className = 'therm-target';
      targetMarker.style.cssText = 'position:absolute;left:0;right:0;height:0;border-top:2px dashed #ffd27a;display:none;pointer-events:none;';
      track.append(fill, targetMarker);
      tube.append(scaleEl, track, bulb);

      const tempLabel = document.createElement('div');
      tempLabel.className = 'therm-value';
      tempLabel.textContent = '--°C';

      wrap.append(title, tube, tempLabel);
      return { wrap, fill, tempLabel, targetMarker };
    };

    const insideTherm = createThermometer('CABIN');
    insideThermFill = insideTherm.fill;
    insideThermLabel = insideTherm.tempLabel;
    insideThermTarget = insideTherm.targetMarker;

    const outsideTherm = createThermometer('SEA');
    outsideThermFill = outsideTherm.fill;
    outsideThermLabel = outsideTherm.tempLabel;

    const thermRow = document.createElement('div');
    thermRow.className = 'therm-row';
    thermRow.append(insideTherm.wrap, outsideTherm.wrap);
    temperatureCard.append(thermRow);

    const adjustCard = createCard('Generator controls', 'Adjust oxygen generation in fixed one-percent steps.');
    adjustCard.classList.add('ctrl-room-bottom-card', 'oxygen-generator-control-card');

    const levelReadout = document.createElement('div');
    levelReadout.className = 'oxygen-generator-level-readout';
    levelReadout.textContent = `${Math.round(submarine.oxygenGeneratorLevel)}%`;
    oxygenGeneratorLevelValue = levelReadout;

    let requestedLevel = Math.round(submarine.oxygenGeneratorLevel);
    const emitLevel = (nextLevel: number): void => {
      if (!controllerState.currentRoomCode) {
        return;
      }
      requestedLevel = clamp(nextLevel, OXYGEN_GENERATOR_MIN_LEVEL, OXYGEN_GENERATOR_MAX_LEVEL);
      emitControl({ action: 'oxygen_set_level', value: requestedLevel });
      levelReadout.textContent = `${requestedLevel}%`;
    };

    const oxygenGeneratorControl = createPlusMinusControl(
      'O2 generator output',
      {
        title: 'Decrease generator level',
        ariaLabel: 'Decrease generator level',
        onStep: () => updateGeneratorLevel(-1)
      },
      {
        title: 'Increase generator level',
        ariaLabel: 'Increase generator level',
        onStep: () => updateGeneratorLevel(1)
      },
      { buttonRowClassName: 'oxygen-generator-button-row' }
    );

    const oxygenLocked = !controllerState.lastRoomState?.ownedUpgrades.includes('unlock-oxygen');
    const updateGeneratorControlState = (): void => {
      oxygenGeneratorControl.decreaseButton.disabled = oxygenLocked || requestedLevel <= OXYGEN_GENERATOR_MIN_LEVEL;
      oxygenGeneratorControl.increaseButton.disabled = oxygenLocked || requestedLevel >= OXYGEN_GENERATOR_MAX_LEVEL;
    };
    if (oxygenLocked) {
      const note = document.createElement('div');
      note.style.cssText = 'font-size:0.75rem;opacity:0.75;margin-top:0.3rem;';
      note.textContent = 'Locked: the generator runs automatically. Buy the Oxygen Controls upgrade (unlock-oxygen) to adjust it.';
      adjustCard.append(note);
    }

    const updateGeneratorLevel = (delta: number): void => {
      emitLevel(requestedLevel + delta);
      updateGeneratorControlState();
    };

    updateGeneratorControlState();

    oxygenGeneratorDecreaseButton = oxygenGeneratorControl.decreaseButton;
    oxygenGeneratorIncreaseButton = oxygenGeneratorControl.increaseButton;
    adjustCard.append(levelReadout, oxygenGeneratorControl.wrap);

    const appendLeft = ctrlZoneLeftBottom
      ? (els: HTMLElement[]) => ctrlZoneLeftBottom!.append(...els)
      : (els: HTMLElement[]) => controls!.append(...els);

    const appendRight = ctrlZoneRightAux
      ? (els: HTMLElement[]) => ctrlZoneRightAux!.append(...els)
      : appendLeft;

    appendLeft([adjustCard]);

    // The heater switch always sits bottom-right; without the heating upgrade it is shown disabled.
    const heaterInstalled = Boolean(controllerState.lastRoomState?.ownedUpgrades.includes('unlock-heating'));
    const heaterCard = createCard('Heater', `Cabin heater output, ${HEATER_LEVELS} levels. Each level raises the cabin's target temperature (level 3 holds 10°C in -50°C water) and draws more power while heating.`);
    heaterCard.classList.add('ctrl-room-bottom-card', 'life-support-heater-control-card');
    const heaterLevelNow = (): number => controllerState.lastSnapshot?.submarine.heaterLevel
      ?? (controllerState.lastSnapshot?.submarine.heaterOn ? 3 : 0);
    const setHeaterLevel = (level: number): void => {
      if (!controllerState.currentRoomCode || !controllerState.lastSnapshot) {
        return;
      }
      emitControl({ action: 'set_heater_level', value: Math.max(0, Math.min(HEATER_LEVELS, level)) });
    };
    const heaterControl = createPlusMinusControl(
      heaterInstalled ? 'Heater' : 'Heater (automatic) - locked: needs unlock-heating',
      { title: 'Heater down', ariaLabel: 'Decrease heater level', onStep: () => setHeaterLevel(heaterLevelNow() - 1), disabled: !heaterInstalled },
      { title: 'Heater up', ariaLabel: 'Increase heater level', onStep: () => setHeaterLevel(heaterLevelNow() + 1), disabled: !heaterInstalled },
      { buttonRowClassName: 'life-support-heater-button-row' }
    );
    const segmentRow = document.createElement('div');
    segmentRow.style.cssText = 'display:flex;gap:4px;width:100%;margin:0.4rem 0;';
    const segments: HTMLElement[] = [];
    for (let i = 0; i < HEATER_LEVELS; i += 1) {
      const segment = document.createElement('div');
      segment.style.cssText = 'flex:1;height:14px;border-radius:3px;background:rgba(255,255,255,0.08);transition:background 0.2s;';
      segments.push(segment);
      segmentRow.append(segment);
    }
    const heaterPower = document.createElement('div');
    heaterPower.style.cssText = 'display:flex;justify-content:space-between;font-size:0.8rem;letter-spacing:0.08em;opacity:0.85;margin-bottom:0.3rem;';
    const heaterPowerLabel = document.createElement('span');
    heaterPowerLabel.textContent = 'POWER DRAW';
    heaterPowerValue = document.createElement('span');
    heaterPowerValue.textContent = '0.0 MW';
    heaterPower.append(heaterPowerLabel, heaterPowerValue);
    const heaterStatus = document.createElement('div');
    heaterStatus.className = 'life-support-heater-status';
    heaterStatus.style.cssText = 'text-align:center;font-size:0.8rem;letter-spacing:0.08em;opacity:0.85;';
    heaterStatus.textContent = heaterInstalled ? 'OFF' : '';
    heaterUi = heaterInstalled
      ? { decrease: heaterControl.decreaseButton, increase: heaterControl.increaseButton, segments, status: heaterStatus }
      : null;
    heaterCard.append(segmentRow, heaterPower, heaterStatus, heaterControl.wrap);
    appendRight([heaterCard]);

    card.classList.add('life-support-oxygen-panel');
    addScrewCorners(card);

    const tabBar = document.createElement('div');
    tabBar.className = 'life-support-tab-bar';
    tabButtons.clear();
    tabPanels.clear();

    for (const tab of LIFE_SUPPORT_TABS) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'life-support-tab-btn';
      btn.textContent = tab.label;
      btn.addEventListener('click', () => setActiveTab(tab.id));
      tabButtons.set(tab.id, btn);
      tabBar.append(btn);
    }

    const oxygenPanel = document.createElement('div');
    oxygenPanel.className = 'life-support-tab-panel';
    oxygenPanel.append(card);
    tabPanels.set('oxygen', oxygenPanel);

    const tempPanel = document.createElement('div');
    tempPanel.className = 'life-support-tab-panel';
    tempPanel.append(temperatureCard);
    tabPanels.set('temp', tempPanel);

    const noisePanel = document.createElement('div');
    noisePanel.className = 'life-support-tab-panel';
    noisePanel.append(buildNoisePanel());
    tabPanels.set('noise', noisePanel);

    const tabContainer = document.createElement('div');
    tabContainer.className = 'life-support-tab-container';
    tabContainer.append(tabBar, oxygenPanel, tempPanel, noisePanel);
    controls.append(tabContainer);

    setActiveTab(activeTab);
    updateUi(snapshot);
  };

  return {
    render,
    updateUi
  };
};

/** `processorLoad`: 0 = processor idle, 1 = ordinary craft, UPGRADE_CRAFT_LOAD_MULTIPLIER = building an upgrade. */
export const formatLifeSupportConsoleOutput = (snapshot: GameSnapshot, crewCount: number, processorLoad: number | boolean = 0): string => {
  const submarine = snapshot.submarine;
  const paused = submarine.docked;
  const crewUse = paused ? 0 : crewCount * OXYGEN_CREW_USE_PER_MINUTE;
  const engineUse = paused ? 0 : submarine.engineOutput * OXYGEN_ENGINE_USE_FACTOR;
  const load = processorLoad === true ? 1 : processorLoad || 0;
  const processingUse = paused ? 0 : OXYGEN_PROCESSING_USE_PER_MINUTE * load;
  const totalUse = crewUse + engineUse + processingUse;
  const generation = submarine.engineOverloaded || paused
    ? 0
    : submarine.oxygenGeneratorLevel * OXYGEN_GENERATION_PER_LEVEL_PER_MINUTE;
  const drainPerMinute = Math.max(0, totalUse - generation);
  const timeUntilEmpty = drainPerMinute > 0
    ? `${(submarine.oxygen / drainPerMinute).toFixed(1)} min`
    : 'N/A';
  const lw = getConsoleLineWidth();
  const sep = '-'.repeat(lw);

  const nearVent = submarine.outsideTemperature >= VENT_PROXIMITY_TEMPERATURE_THRESHOLD;
  const excessOxygen = !paused && submarine.oxygen >= LIQUID_OXYGEN_EXCESS_THRESHOLD && generation > totalUse;
  const loxStatus = submarine.loxBoostActive
    ? 'feeding engine boost'
    : nearVent && excessOxygen
      ? 'topping up (vent + excess O2)'
      : nearVent
        ? 'topping up (vent)'
        : excessOxygen
          ? 'topping up (excess O2)'
          : 'idle';

  return [
    sep,
    padDots('Current Tank Level:', `${Math.round(submarine.oxygen)}%`, lw),
    padDots('O2 Generation per minute:', `${generation.toFixed(1)}/min`, lw),
    padDots('O2 Use per minute:', `${totalUse.toFixed(1)}/min`, lw),
    padDots('Time until empty:', timeUntilEmpty, lw),
    '',
    padDots('Liquid O2 reserve:', `${Math.round(submarine.liquidOxygen ?? 0)}%`, lw),
    padDots('Liquid O2 status:', loxStatus, lw),
    '',
    sep,
    'O2 use by subsystem:',
    padDots('  - crew:', `${crewUse.toFixed(1)}/min`, lw),
    padDots('  - engine:', `${engineUse.toFixed(1)}/min`, lw),
    padDots('  - material processor:', `${processingUse.toFixed(1)}/min`, lw)
  ].join('\n');
};
